import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import type { WorkoutSet } from "../../../shared/src";
import {
  ApiError,
  addWorkoutSet,
  deleteWorkoutSet,
  getExerciseProgression,
  getPreviousSetsForExercise,
  reorderWorkoutSets,
  updateWorkoutSet,
} from "../api-client";
import { ExerciseTrend } from "./ExerciseTrend";

interface ExerciseCardProps {
  workoutId: number;
  exerciseId: number;
  exerciseName: string;
  sets: WorkoutSet[];
  targets?: {
    sets: number | null;
    reps: number | null;
    weight_kg: number | null;
  };
  disabled?: boolean;
  onSetAdded?: (exerciseId: number) => void;
}

export function ExerciseCard({
  workoutId,
  exerciseId,
  exerciseName,
  sets,
  targets,
  disabled = false,
  onSetAdded,
}: ExerciseCardProps) {
  const queryClient = useQueryClient();
  const setsKey = ["workoutSets", workoutId] as const;

  const addMutation = useMutation({
    mutationFn: (input: { weight_kg: number; reps: number }) =>
      addWorkoutSet(workoutId, { exercise_id: exerciseId, ...input }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setsKey });
      onSetAdded?.(exerciseId);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: {
      id: number;
      patch: { weight_kg?: number; reps?: number };
    }) => updateWorkoutSet(input.id, input.patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: setsKey }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteWorkoutSet(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: setsKey }),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: number[]) =>
      reorderWorkoutSets(workoutId, exerciseId, ids),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: setsKey }),
    onError: () => queryClient.invalidateQueries({ queryKey: setsKey }),
  });

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
  );

  function onSetDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = sets.map((s) => s.id);
    const from = ids.indexOf(Number(active.id));
    const to = ids.indexOf(Number(over.id));
    if (from < 0 || to < 0) return;
    const next = arrayMove(ids, from, to);
    queryClient.setQueryData<WorkoutSet[]>(setsKey, (prev) => {
      if (!prev) return prev;
      const byId = new Map(prev.map((s) => [s.id, s]));
      const reordered = next.map((id, i) => {
        const s = byId.get(id);
        return s ? { ...s, set_index: i + 1 } : null;
      });
      const survivors = reordered.filter((s): s is WorkoutSet => s !== null);
      const others = prev.filter((s) => s.exercise_id !== exerciseId);
      return [...others, ...survivors];
    });
    reorderMutation.mutate(next);
  }

  const previousQuery = useQuery({
    queryKey: ["previousSets", workoutId, exerciseId] as const,
    queryFn: () => getPreviousSetsForExercise(workoutId, exerciseId),
  });

  const progressionQuery = useQuery({
    queryKey: ["progression", exerciseId] as const,
    queryFn: () => getExerciseProgression(exerciseId),
  });

  return (
    <section
      aria-label={`exercise ${exerciseName}`}
      className="rounded-lg border border-slate-800 bg-slate-900 p-4 space-y-3"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-medium">{exerciseName}</h3>
          {targets && (
            <p className="text-xs text-slate-500">{formatTargets(targets)}</p>
          )}
        </div>
        {progressionQuery.data && (
          <ExerciseTrend
            points={progressionQuery.data}
            currentWorkoutId={workoutId}
          />
        )}
      </header>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <p className="text-xs text-slate-500 mb-1">This session</p>
          {sets.length === 0 ? (
            <p className="text-sm text-slate-400">No sets logged yet.</p>
          ) : (
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              onDragEnd={onSetDragEnd}
            >
              <SortableContext
                items={sets.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul className="space-y-2">
                  {sets.map((s) => (
                    <SetRow
                      key={s.id}
                      set={s}
                      onUpdate={(patch) =>
                        updateMutation.mutateAsync({ id: s.id, patch })
                      }
                      onDelete={() => deleteMutation.mutate(s.id)}
                      disabled={disabled}
                      reorderDisabled={
                        disabled || sets.length < 2 || reorderMutation.isPending
                      }
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}
        </div>
        <div aria-label="last session sets">
          <p className="text-xs text-slate-500 mb-1">Last session</p>
          {previousQuery.data === undefined ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : previousQuery.data.length === 0 ? (
            <p className="text-sm text-slate-400">
              No prior sets for this exercise.
            </p>
          ) : (
            <ul className="space-y-2">
              {previousQuery.data.map((s) => (
                <li
                  key={s.id}
                  className="rounded-md border border-slate-800 bg-slate-950 p-2 text-sm text-slate-300"
                >
                  <span className="text-xs text-slate-500 mr-2">
                    #{s.set_index}
                  </span>
                  {s.weight_kg ?? 0} kg × {s.reps ?? 0} reps
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <AddSetForm
        key={`add-${sets.length}`}
        initialWeight={initialWeightFor(sets, targets)}
        initialReps={initialRepsFor(sets, targets)}
        onAdd={(input) => addMutation.mutateAsync(input)}
        disabled={disabled || addMutation.isPending}
        error={addMutation.isError ? messageFor(addMutation.error) : null}
      />
    </section>
  );
}

function initialWeightFor(
  sets: WorkoutSet[],
  targets: ExerciseCardProps["targets"],
): string {
  const last = sets.at(-1);
  if (last && last.weight_kg !== null) return String(last.weight_kg);
  if (
    targets &&
    targets.weight_kg !== null &&
    targets.weight_kg !== undefined
  ) {
    return String(targets.weight_kg);
  }
  return "";
}

function initialRepsFor(
  sets: WorkoutSet[],
  targets: ExerciseCardProps["targets"],
): string {
  const last = sets.at(-1);
  if (last && last.reps !== null) {
    return String(Math.max(1, last.reps - 2));
  }
  if (targets && targets.reps !== null && targets.reps !== undefined) {
    return String(targets.reps);
  }
  return "";
}

interface AddSetFormProps {
  initialWeight: string;
  initialReps: string;
  onAdd: (input: { weight_kg: number; reps: number }) => Promise<unknown>;
  disabled: boolean;
  error: string | null;
}

function AddSetForm({
  initialWeight,
  initialReps,
  onAdd,
  disabled,
  error,
}: AddSetFormProps) {
  const [weight, setWeight] = useState(initialWeight);
  const [reps, setReps] = useState(initialReps);
  const [localError, setLocalError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const w = Number(weight);
    const r = Number(reps);
    if (!Number.isFinite(w) || w < 0) {
      setLocalError("Weight must be 0 or greater.");
      return;
    }
    if (!Number.isInteger(r) || r < 1) {
      setLocalError("Reps must be a positive whole number.");
      return;
    }
    setLocalError(null);
    try {
      await onAdd({ weight_kg: w, reps: r });
      // Inputs reset by remount: the parent re-keys the form on sets.length.
    } catch {
      // Mutation error surfaces via the `error` prop from the parent.
    }
  }

  const message = localError ?? error;
  const canSubmit =
    !disabled && weight.trim().length > 0 && reps.trim().length > 0;

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-wrap items-end gap-2"
      aria-label="add set"
    >
      <label className="flex flex-col text-xs text-slate-400">
        <span>Weight (kg)</span>
        <input
          type="number"
          inputMode="decimal"
          step="0.5"
          min="0"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          aria-label="weight kg"
          className="mt-1 w-24 rounded-md border border-slate-700 bg-slate-950 px-3 py-3 text-base"
        />
      </label>
      <label className="flex flex-col text-xs text-slate-400">
        <span>Reps</span>
        <input
          type="number"
          inputMode="numeric"
          step="1"
          min="1"
          value={reps}
          onChange={(e) => setReps(e.target.value)}
          aria-label="reps"
          className="mt-1 w-20 rounded-md border border-slate-700 bg-slate-950 px-3 py-3 text-base"
        />
      </label>
      <button
        type="submit"
        disabled={!canSubmit}
        className="h-12 rounded-md bg-orange-600 px-4 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50"
      >
        Add set
      </button>
      {message && (
        <p className="w-full text-sm text-red-400" role="alert">
          {message}
        </p>
      )}
    </form>
  );
}

interface SetRowProps {
  set: WorkoutSet;
  onUpdate: (patch: { weight_kg?: number; reps?: number }) => Promise<unknown>;
  onDelete: () => void;
  disabled: boolean;
  reorderDisabled: boolean;
}

function SetRow({
  set,
  onUpdate,
  onDelete,
  disabled,
  reorderDisabled,
}: SetRowProps) {
  const [editing, setEditing] = useState(false);
  const [weight, setWeight] = useState(String(set.weight_kg ?? ""));
  const [reps, setReps] = useState(String(set.reps ?? ""));
  const [error, setError] = useState<string | null>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: set.id, disabled: reorderDisabled });
  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 10 : "auto",
  } as const;

  async function commit() {
    const w = Number(weight);
    const r = Number(reps);
    const patch: { weight_kg?: number; reps?: number } = {};
    if (Number.isFinite(w) && w !== set.weight_kg) patch.weight_kg = w;
    if (Number.isInteger(r) && r !== set.reps) patch.reps = r;
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setError(null);
    try {
      await onUpdate(patch);
      setEditing(false);
    } catch (err) {
      setError(messageFor(err));
    }
  }

  if (editing) {
    return (
      <li
        ref={setNodeRef}
        style={dragStyle}
        className="flex flex-wrap items-center gap-2 rounded-md border border-slate-800 bg-slate-950 p-2"
        aria-label={`edit set ${set.set_index}`}
      >
        <span className="text-xs text-slate-400 w-8">#{set.set_index}</span>
        <input
          type="number"
          inputMode="decimal"
          step="0.5"
          min="0"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          onBlur={commit}
          aria-label={`weight set ${set.set_index}`}
          className="w-20 rounded-md border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        />
        <span className="text-xs text-slate-500">kg ×</span>
        <input
          type="number"
          inputMode="numeric"
          step="1"
          min="1"
          value={reps}
          onChange={(e) => setReps(e.target.value)}
          onBlur={commit}
          aria-label={`reps set ${set.set_index}`}
          className="w-16 rounded-md border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        />
        <button
          type="button"
          onClick={commit}
          className="rounded-md bg-orange-600 px-2 py-1 text-xs font-medium text-white hover:bg-orange-500"
        >
          Save
        </button>
        {error && (
          <p className="w-full text-xs text-red-400" role="alert">
            {error}
          </p>
        )}
      </li>
    );
  }

  return (
    <li
      ref={setNodeRef}
      style={dragStyle}
      className="flex items-center justify-between gap-2 rounded-md border border-slate-800 bg-slate-950 p-2"
    >
      <button
        type="button"
        aria-label={`reorder set ${set.set_index}`}
        disabled={reorderDisabled}
        className="cursor-grab touch-none rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-400 hover:bg-slate-800 active:cursor-grabbing disabled:opacity-30"
        {...attributes}
        {...listeners}
      >
        ⠿
      </button>
      <button
        type="button"
        onClick={() => !disabled && setEditing(true)}
        className="flex-1 text-left text-sm"
      >
        <span className="text-xs text-slate-400 mr-2">#{set.set_index}</span>
        <span>
          {set.weight_kg ?? 0} kg × {set.reps ?? 0} reps
        </span>
      </button>
      <button
        type="button"
        onClick={onDelete}
        disabled={disabled}
        aria-label={`delete set ${set.set_index}`}
        className="rounded-md border border-red-900 px-2 py-1 text-xs text-red-300 hover:bg-red-950 disabled:opacity-30"
      >
        Delete
      </button>
    </li>
  );
}

function formatTargets(t: {
  sets: number | null;
  reps: number | null;
  weight_kg: number | null;
}): string {
  const parts: string[] = [];
  if (t.sets !== null) parts.push(`${t.sets} sets`);
  if (t.reps !== null) parts.push(`${t.reps} reps`);
  if (t.weight_kg !== null) parts.push(`${t.weight_kg} kg`);
  return parts.length === 0 ? "No targets set" : parts.join(" × ");
}

function messageFor(err: unknown): string {
  if (
    err instanceof ApiError &&
    (err.code === "BAD_REQUEST" ||
      err.code === "UNPROCESSABLE_ENTITY" ||
      err.code === "CONFLICT")
  ) {
    return err.message;
  }
  return "Something went wrong. Try again.";
}
